/**
 * RadioGroup + Radio — Base UI Radio primitive.
 *
 * Design system mapping (mch-check radio):
 *   • Box: 16x16, border webex-gray-500 / dark: webex-gray-400, rounded-full
 *   • Hover border: webex-blue-600
 *   • Checked: 5px webex-blue-600 ring (border-5 + bg-white)
 *   • Focus: ring-2 webex-blue-600 ring-offset-2
 *
 * Composition:
 *   <RadioGroup defaultValue="a" name="plan">
 *     <RadioOption value="a">Plan A</RadioOption>
 *     <RadioOption value="b">Plan B</RadioOption>
 *   </RadioGroup>
 */
import * as React from "react"
import { RadioGroup as BaseRadioGroup } from "@base-ui-components/react/radio-group"
import { Radio as BaseRadio } from "@base-ui-components/react/radio"
import { cn } from "@lib/cn"

export interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof BaseRadioGroup> {
  orientation?: "vertical" | "horizontal"
}

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ className, orientation = "vertical", ...props }, ref) => {
    return (
      <BaseRadioGroup
        ref={ref}
        className={cn(
          "flex gap-3",
          orientation === "vertical" ? "flex-col" : "flex-row flex-wrap",
          className,
        )}
        {...props}
      />
    )
  },
)
RadioGroup.displayName = "RadioGroup"

export interface RadioOptionProps
  extends React.ComponentPropsWithoutRef<typeof BaseRadio.Root> {
  children?: React.ReactNode
  labelClassName?: string
}

export const RadioOption = React.forwardRef<HTMLButtonElement, RadioOptionProps>(
  ({ className, labelClassName, children, value, id, ...props }, ref) => {
    const reactId = React.useId()
    const labelId = `${id ?? reactId}-label`
    const radioId = id ?? `${reactId}-radio`
    return (
      <label
        htmlFor={radioId}
        className="inline-flex items-center gap-2 text-sm text-webex-gray-1000 dark:text-white cursor-pointer select-none"
      >
        <BaseRadio.Root
          ref={ref as React.Ref<HTMLElement>}
          id={radioId}
          value={value}
          aria-labelledby={children ? labelId : undefined}
          aria-label={!children && typeof value === "string" ? value : undefined}
          className={cn(
            "inline-flex size-4 shrink-0 items-center justify-center rounded-full",
            "border border-webex-gray-500 dark:border-webex-gray-400",
            "bg-white dark:bg-webex-gray-950",
            "transition-colors duration-100 motion-reduce:transition-none cursor-pointer",
            "hover:border-webex-blue-600",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-webex-gray-950",
            "data-[checked]:border-[5px] data-[checked]:border-webex-blue-600",
            "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed",
            className,
          )}
          {...props}
        >
          <BaseRadio.Indicator className="size-full" />
        </BaseRadio.Root>
        {children && (
          <span id={labelId} className={labelClassName}>
            {children}
          </span>
        )}
      </label>
    )
  },
)
RadioOption.displayName = "RadioOption"
